import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Typography, Stack, TextField, InputAdornment, IconButton } from '@mui/material';
import Iconify from '../../components/iconify/Iconify';
import { LoadingButton } from '@mui/lab';
import Swal from 'sweetalert2';
import { styled } from '@mui/material/styles';
import Api from '../../utils/Api';
import CustomizedProgressBars from '../../components/loader';

// Styled components
const StyledRoot = styled('div')(({ theme }) => ({
    backgroundImage: 'url(https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f300b982243934e5c9ae8a_AdobeStock_535186049_gruen_klein.webp)',
    backgroundSize: 'cover',
    backgroundColor: '#000000',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
}));

const StyledContent = styled('form')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    padding: '28px',
    boxShadow: 'rgba(11, 211, 73, 0.42) 0px 18px 40px 4px',
    borderRadius: '10px',
    backgroundColor: 'rgb(0,0,0,0.9)'
}));

export default function ResetPassword() {
    const navigate = useNavigate();
    const { token } = useParams(); // Get token from URL params
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleClick = async (ev) => {
        ev.preventDefault();
        if (formData.newPassword !== formData.confirmPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Passwortkonflikt',
                text: 'Das neue Passwort und das Bestätigungspasswort stimmen nicht überein.',
                iconColor: '#000',
                confirmButtonColor: '#000',
            });
            return;
        }

        try {
            setIsLoading(true);
            const response = await Api.post('/reset-password', { token, newPassword: formData.newPassword });
            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Erfolg',
                    text: 'Ihr Passwort wurde erfolgreich zurückgesetzt.',
                    iconColor: '#000',
                    confirmButtonColor: '#000',
                });
                navigate('/');
            }
        } catch (error) {
            console.error('Error during password reset:', error);
            Swal.fire({
                icon: 'error',
                title: 'Zurücksetzen fehlgeschlagen',
                text: error.response ? error.response.data.message : 'Etwas ist schief gelaufen!',
                iconColor: '#000',
                confirmButtonColor: '#000',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StyledRoot>
            <Container maxWidth="sm">
                <StyledContent onSubmit={handleClick}>
                    <img
                        src="/assets/ennovation_logo.png"
                        alt="Logo"
                        sx={{
                            display: 'block',
                            margin: '0 auto',
                            height: { xs: '80px', sm: '100px', md: '120px' }, // Adjust height based on screen size
                            width: { xs: '200px', sm: '300px', md: '380px' }, // Adjust width based on screen size
                            objectFit: 'contain',
                        }}
                    />
                    <Typography variant="h4" gutterBottom style={{ textAlign: 'center', paddingBottom: '20px', marginTop: '10px' }}>
                        {/* Reset Password */}
                        Passwort zurücksetzen
                    </Typography>
                    <Stack spacing={3} style={{ marginBottom: '30px' }}>
                        <TextField
                            type={showPassword ? 'text' : 'password'}
                            name="newPassword"
                            // label="New Password"
                            label="Neues Passwort"
                            onChange={handleChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            type={showPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            // label="Confirm Password"
                            label="Passwort bestätigen"
                            onChange={handleChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                    <LoadingButton
                        sx={{
                            boxShadow: 'none',
                            '&:focus': {
                                boxShadow: 'none', // Remove the blue shadow
                            },
                            '&:hover': {
                                boxShadow: 'none', // Optionally remove shadow on hover too
                            },
                        }}
                        fullWidth
                        size="large"
                        variant="contained"
                        type='submit'
                        style={{ backgroundColor: '#0BD349', color: '#000000' }}
                    >
                        {/* Reset Password */}
                        Passwort zurücksetzen
                    </LoadingButton>
                    {isLoading && <CustomizedProgressBars />}
                </StyledContent>
            </Container>
        </StyledRoot>
    );
}